import { computed } from 'vue'

import type { KanbanLogActor } from '@entities/kanban-comment'

import { useListsStore } from '@shared/model'

interface TutorKanbanLogStageDetailsPayload {
  actor: KanbanLogActor | null
  oldValue: string
  value: string
}

interface TutorKanbanLogStageDetailsReturn {
  refLabel: ComputedRef<string>
  refReasons: ComputedRef<string>
  refOldReasons: ComputedRef<string>
  refCategories: ComputedRef<string>
  refOldCategories: ComputedRef<string>
}

const parseDetails = (json: string | null) => {
  if (!json) return null
  try {
    return JSON.parse(json)
  } catch {
    return null
  }
}

export const useTutorKanbanLogStageDetails = (payload: TutorKanbanLogStageDetailsPayload): TutorKanbanLogStageDetailsReturn => {
  const listsStore = useListsStore()

  const refDetails = computed(() => parseDetails(payload.value))
  const refOldDetails = computed(() => parseDetails(payload.oldValue))

  const refReasons = computed<string>(() => {
    const reasons = refDetails.value?.reasons
    if (!Array.isArray(reasons) || reasons.length === 0) return ''
    const list = listsStore.getLists?.tutor_stage_reason?.all ?? {}
    return reasons.map((r: string) => list[r] ?? r).join(', ')
  })

  const refOldReasons = computed<string>(() => {
    const reasons = refOldDetails.value?.reasons
    if (!reasons) return ''
    const values = Array.isArray(reasons) ? reasons : Object.values(reasons)
    if (values.length === 0) return ''
    const list = listsStore.getLists?.tutor_stage_reason?.all ?? {}
    return values.map((r: string) => list[r] ?? r).join(', ')
  })

  const refCategories = computed<string>(() => {
    const categories = refDetails.value?.categories
    if (!Array.isArray(categories) || categories.length === 0) return ''
    const list = listsStore.getLists?.tutor_stage_category?.all ?? {}
    return categories.map((c: string) => list[c] ?? c).join(', ')
  })

  const refOldCategories = computed<string>(() => {
    const categories = refOldDetails.value?.categories
    if (!categories) return ''
    const values = Array.isArray(categories) ? categories : Object.values(categories)
    if (values.length === 0) return ''
    const list = listsStore.getLists?.tutor_stage_category?.all ?? {}
    return values.map((c: string) => list[c] ?? c).join(', ')
  })

  const refIsAdd = computed<boolean>(() => !refOldReasons.value && !refOldCategories.value)
  const refLabel = computed<string>(() => refIsAdd.value ? 'add stage details' : 'update stage details')

  return {
    refLabel,
    refReasons,
    refOldReasons,
    refCategories,
    refOldCategories,
  }
}
